// apps/account-portal/src/pages/SystemSubscriptionPage.tsx
import React, { useContext, useEffect, useState } from 'react';
import { Alert, ConfirmModal } from '@redpanda/ui';
import { useTranslation } from 'react-i18next';
import { SystemType } from '@redpanda/types';
import { useSubscription } from '../stores/subscriptionStore';
import CheckoutModal from '../components/CheckoutModal';
import PricingTable from '../components/PricingTable';
import SubscriptionInfoCard from '../components/SubscriptionInfoCard';
import PageContainer from '../components/PageContainer';
import PageTitle from '../components/PageTitle';
import PageFooter from '../components/PageFooter';
import { UserContext } from '../context/UserContext';
import { redirectToLogin } from '@redpanda/auth-utils';
import { IDENTITY_SERVICE_URL } from '../constants/publicPaths';
import { Plan } from '@redpanda/types';

interface SubscriptionPageProps {
  system: SystemType;
}

const SubscriptionPage: React.FC<SubscriptionPageProps> = ({ system }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const { t } = useTranslation();
  // 方案與價格公開瀏覽；目前訂閱只有登入後才查、才顯示，選方案時沒登入就先去登入
  const { isAuthenticated } = useContext(UserContext)!;

  const systemName = system.toLowerCase();

  // 使用 subscriptionStore 的快取功能
  const {
    subscription,
    plans,
    isLoadingPlans,
    plansError,
    subscriptionsLoaded,
    subscriptionsError: error,
    isCanceling,
    fetchSubscriptions,
    fetchSystemPlans,
    cancelSubscription,
    clearErrors
  } = useSubscription(system);

  // Handle data fetching - 簡化邏輯，仿照 CRM DashboardPage
  useEffect(() => {
    if (isAuthenticated) fetchSubscriptions();
    fetchSystemPlans();
  }, [isAuthenticated, fetchSubscriptions, fetchSystemPlans]);


  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!subscription) return;

    try {
      await cancelSubscription(subscription.id);
      setShowCancelModal(false);
    } catch (err: any) {
      console.error('Cancel subscription error:', err);
    }
  };

  const handleCancelModalClose = () => {
    if (!isCanceling) {
      setShowCancelModal(false);
      clearErrors();
    }
  };

  const handleChoosePlan = (plan: Plan) => {
    if (!isAuthenticated) return redirectToLogin(IDENTITY_SERVICE_URL, window.location.href);
    setSelectedPlan(plan);
    setShowCheckoutModal(true);
  };

  const handleCloseCheckoutModal = () => {
    setShowCheckoutModal(false);
    setSelectedPlan(null);
  };

  const handlePaymentSuccess = () => {
    // 滾動到頂部讓用戶看到更新
    window.scrollTo(0, 0);
  };


  return (
    <PageContainer>
      <PageTitle title={t(`systems.${systemName}.subscription_title`)} />

      {error && (
        <Alert variant="error" title={t('common.status.error')} className="mb-6">
          {error}
        </Alert>
      )}

      {/* 訂閱資訊區域（登入後才有） */}
      {isAuthenticated && (
        <SubscriptionInfoCard
          subscription={subscription}
          subscriptionsLoaded={subscriptionsLoaded}
          isCanceling={isCanceling}
          onCancelClick={handleCancelClick}
        />
      )}

      {/* 方案選擇區域 */}
      <div className="mt-4 pricing-section">

        {/* 方案載入狀態 */}
        {isLoadingPlans ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto mb-4"></div>
            <p className="text-[var(--text-secondary)]">{t('common.status.loading')}</p>
          </div>
        ) : (
          <div>
            {plansError && (
              <Alert variant="error" title={t('common.status.error')} className="mb-6">
                {plansError}
              </Alert>
            )}

            {plans.length > 0 ? (
              <PricingTable
                system={system}
                plans={plans}
                onChoosePlan={handleChoosePlan}
                currentSubscription={isAuthenticated ? subscription : null}
                isGuest={!isAuthenticated}
              />
            ) : (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
                  {t('pricing.no_plans_available')}
                </h3>
                <p className="text-[var(--text-secondary)]">
                  {t('pricing.no_plans_description')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 結帳Modal */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={handleCloseCheckoutModal}
        plan={selectedPlan}
        system={system}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* 取消訂閱確認對話框 */}
      <ConfirmModal
        isOpen={showCancelModal}
        onClose={handleCancelModalClose}
        onConfirm={handleCancelConfirm}
        title={t('subscription.cancel_confirm_title')}
        message={t('subscription.cancel_confirm_message', { system: t(`systems.${systemName}.name`) })}
        confirmText={t('subscription.cancel_confirm_button')}
        type="danger"
        isLoading={isCanceling}
        t={t}
      />
      <PageFooter />
    </PageContainer>
  );
};

export default SubscriptionPage;